import type {ContentMediaEditorialDto} from '@devmil/contracts';
import {useId, useState} from 'react';

import {
  EDITORIAL_MEDIA_CRITERION_LABEL,
  EDITORIAL_MEDIA_UNAVAILABLE_LABEL,
  EDITORIAL_RECOMMENDATION_CLASS,
  EDITORIAL_RECOMMENDATION_LABEL,
  editorialMediaCoverageLabel,
  formatDateTime,
} from '../../lib/format.js';

/**
 * O parecer da MÍDIA, ao lado do parecer do texto e nunca dentro dele.
 *
 * ## O achado que fez este bloco existir
 *
 * A auditoria de 06/09/2026 mediu, no achado F11, que a revisão visual era
 * produzida, PAGA e gravada desde o lote 03, e nunca chegava a quem decide. A
 * revisão individual mostrava um bloco chamado "Parecer editorial" alimentado
 * só pelo parecer de TEXTO. Quem abria a tela lia uma nota sobre o roteiro
 * achando que ela cobria a peça.
 *
 * ## Por que dois blocos, e não um
 *
 * Duas razões, e nenhuma é estilo:
 *
 * 1. **As réguas são diferentes.** O texto tem cinco dimensões e vale até 20; a
 *    mídia tem três e vale até 12. Uma nota "9" sem dizer sobre quantos não
 *    significa nada, e somar as duas produziria um número que não existe em
 *    régua nenhuma.
 * 2. **As âncoras são diferentes.** O parecer do texto vence quando o texto
 *    muda; o da mídia vence quando a mídia OU o texto mudam. Fundidos, uma
 *    regeneração de mídia apagaria da tela uma avaliação de texto que continua
 *    válida.
 *
 * ## A linha que não é enfeite
 *
 * A COBERTURA, logo abaixo da nota. Ela diz o que foi olhado, e é a defesa
 * contra o erro de leitura mais caro desta tela: um parecer favorável sobre a
 * CAPA de um Reel, lido como parecer sobre o vídeo, é uma nota favorável que
 * ninguém deu. Quando foi só a capa, a tela escreve "capa". Quando foram
 * amostras, ela escreve quantas telas de quantas, e que o vídeo não foi
 * assistido. Nenhum caminho desta esteira assiste ao vídeo, e no contrato
 * `videoReviewed` é `false` literal, e não um booleano que alguém possa virar.
 */
export const MediaOpinion = ({
  editorial,
}: {
  editorial: ContentMediaEditorialDto;
}) => {
  const headingId = useId();
  const detalheId = useId();
  const [aberto, setAberto] = useState(false);

  if (editorial.availability === 'unavailable') {
    return (
      <section className="dm-editorial" aria-labelledby={headingId}>
        <h4 id={headingId}>Parecer da mídia</h4>
        <p className="dm-editorial-vazio">
          {EDITORIAL_MEDIA_UNAVAILABLE_LABEL[editorial.reason]}
        </p>
      </section>
    );
  }

  return (
    <section className="dm-editorial" aria-labelledby={headingId}>
      <h4 id={headingId}>Parecer da mídia</h4>
      <p className="dm-editorial-veredito">
        <strong
          className={EDITORIAL_RECOMMENDATION_CLASS[editorial.recommendation]}
        >
          {EDITORIAL_RECOMMENDATION_LABEL[editorial.recommendation]}
        </strong>
        <span>{`${editorial.total} de 12`}</span>
      </p>
      {/*
        A cobertura vem ANTES do achado, de propósito: ela é o que qualifica
        tudo que vem depois. Lida no fim, ela chegaria tarde.
      */}
      <p className="dm-editorial-cobertura">
        {editorialMediaCoverageLabel(editorial.coverage)}
      </p>
      {editorial.mainChange === null ? null : (
        <p className="dm-editorial-mudanca">{editorial.mainChange}</p>
      )}
      {editorial.pendency === null ? null : (
        <p className="dm-editorial-pendencia">
          {`Pendência ${
            editorial.pendency.severity === 'critical' ? 'crítica' : 'menor'
          }: ${editorial.pendency.text}`}
        </p>
      )}
      <p className="dm-editorial-aviso">
        O parecer informa. Quem aprova é você.
      </p>
      <button
        type="button"
        className="dm-editorial-toggle"
        aria-expanded={aberto}
        aria-controls={detalheId}
        onClick={() => setAberto((valor) => !valor)}
      >
        {aberto ? 'Ocultar detalhes' : 'Ver detalhes'}
      </button>
      <div id={detalheId} hidden={!aberto}>
        <ul className="dm-editorial-notas" aria-label="Três dimensões da mídia">
          {editorial.criteria.map((criterio) => (
            <li key={criterio.id}>
              <span>{EDITORIAL_MEDIA_CRITERION_LABEL[criterio.id]}</span>
              <strong>{`${criterio.score}/4`}</strong>
            </li>
          ))}
        </ul>
        {editorial.findings.length === 0 ? (
          <p className="dm-editorial-vazio">Sem achados.</p>
        ) : (
          <ul
            className="dm-editorial-achados"
            aria-label="Achados sobre a mídia"
          >
            {editorial.findings.map((achado, indice) => (
              <li key={`${achado.field}-${indice}`}>
                <span className="dm-editorial-campo">{achado.field}</span>
                {achado.issue}
              </li>
            ))}
          </ul>
        )}
        {/*
          Escopo, data e versões, que o plano pede em cada parecer.

          A DATA é a que mais importa aqui: um parecer de mídia é sobre bytes,
          e saber quando ele foi feito é o que permite ligá-lo à renderização
          que o produziu quando alguém for investigar meses depois.
        */}
        <p className="dm-editorial-versao">
          {`mídia · ${formatDateTime(editorial.evaluatedAt)} · ${
            editorial.rubricVersion
          } · ${editorial.promptVersion} · ${editorial.model}`}
        </p>
      </div>
    </section>
  );
};
